/*================================================================
‘Copyright © 2020, Cheers Interactive Pvt Ltd.  All rights reserved.
      File Description :  Company Listing Page
 ---------------------------------------------------------------------------------
    Creation Details
    Date Created				: 25/Jun/2020
    Author						: Anand Kumar
 ---------------------------------------------------------------------------------
    Change History #1
    Date Modified				: 10/Jul/2020
    Changed By					: Anand Kumar
    Change Description       	: Wire up the company Listing API
    Reason for Change        	: Dynamic listing of company details
================================================================
*/

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { setBreadCrumb } from "../../../middleware/actions/breadCrumbAction";
import { swap, getProductVariant, fetchTechTrendData, getDateFromMongoDate } from "../../../utilities";
import CommonDataGrid from "../../components/dataGrid";
import { StringFilter, DropDownFilter } from "../../components/dataGrid/fliters";
import {
  fetchCompany,
  deleteCompany,
  deleteManyCompany,
  exportJsonData,
  downloadFileUrl,
  saveCompanyTaxonomyTags,
  saveCompanyKiaTags,
  saveValueChainTags,
  saveCompanyBusinessObjectiveTaxonomy,
  saveCompanyTrendTags,
} from "../../../middleware/services/companyApi";
import { showAlertBox } from "../../../middleware/actions/alertBoxAction";
import { actionSuccess, actionError, hideLoader, showLoader } from "../../../middleware/actions/utilityAction";
import { COMMON_ACCESS_ACTION, CONTENT_TYPE, PRODUCT_VARIANT, QUEUE_STATUS } from "../../../constants";
import ThreeDotMenu from "../../components/threeDotMenu";
import _ from "underscore";
import { resetInternalGrid, setMaintainedState, visitRecord } from "../../../middleware/actions/gridAction";
import { resetRedux, setFilteredTaxonomy } from "../../../middleware/actions/taxonomyFilterAction";
import {
  setInitialCheckedKiaNodes,
  setInitialCheckedObjectiveNodes,
  setInitialCheckedSgfNodes,
  setInitialCheckedTechnologyNodes,
  setInitialCheckedTrendNodes,
  setInitialCheckedValueChainNodes,
  setKiaCheckedNodes,
  setKiaUnCheckedNodes,
  setObjectiveTaxonomyCheckedNodes,
  setObjectiveTaxonomyUnCheckedNodes,
  setResetRedux,
  setTaxonomyCheckedNodes,
  setTaxonomyUnCheckedNodes,
  setTransformingCheckedNodes,
  setTransformingUnCheckedNodes,
  setTrendCheckedNodes,
  setTrendUnCheckedNodes,
  setValueChainCheckedNodes,
  setValueChainUnCheckedNodes,
} from "../../../middleware/actions/taxonomyTaggingAction";
import { resetCompany } from "../../../middleware/actions/companyAction";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { fetchConfig } from "middleware/services/cmsApi";

// company dashboard component
const CompanyDashBoard = (props) => {
  const { accessRights: aR } = props;
  const dispatch = useDispatch();
  const taxonomyFilterRedux = useSelector((state) => state.taxonomyFilterState);
  const [deleteId, setDeleteId] = useState(undefined);
  const [techTrendData, setTechTrendData] = useState(undefined);
  const [gridFilters, setGridFilters] = useState([]);
  const [filterData, setFilterData] = useState(taxonomyFilterRedux?.filteredTaxonomy ? taxonomyFilterRedux.filteredTaxonomy : []);
  const productVariant = getProductVariant();
  const history = useHistory();
  const gridState = useSelector((state) => state.gridState);
  const showLoaderGrid = () => dispatch(showLoader());
  const hideLoaderGrid = () => dispatch(hideLoader());
  const moduleName = props.accessRights?.moduleName;
  const [initalState, setInitialState] = useState([]);
  const [bulkCheck, setBulkCheck] = useState([]);
  const [valueChainData, setValueChainData] = useState([]);
  const [showTaxonomyBulk, setShowTaxonomyBulk] = useState(false);
  const [cssClass, setCssClass] = useState("treeClass");
  const [listClass, setListClass] = useState("select-lists");
  const [taxonomyState, setTaxonomyState] = useState([]);
  const [taxonomyTransformationState, setTaxonomyTransformationState] = useState([]);
  const companyTaxonomy = useSelector((state) => state.taxonomyTaggingState);
  const [taxonomyTaggingTabName, setTaxonomyTaggingTabName] = useState(false);
  const [companyContentType, setCompanyContentType] = useState(undefined);
  const [companySubContentType, setCompanySubContentType] = useState(undefined);
  const [companyDeliveryType, setCompanyDeliveryType] = useState(undefined);

  // specify breadcrumb here
  const breadCrumbLinks = [{ linkUrl: "/company", linkName: aR.moduleName, linkActive: true }];

  const fetchContentType = async () => {
    const contentType = JSON.parse(localStorage.getItem("contentType")) || [];
    if (contentType?.length > 0) {
      const resData = contentType
        ?.map((v) => {
          if (v.contentType == CONTENT_TYPE.PROFILES_COMPANIES_TECHNOLOGIES) {
            return { id: v.contentType, text: v.contentType, subContentType: v.subContentType };
          }
        })
        .filter((val) => val !== undefined);

      if (resData) {
        resData.sort((a, b) => a.text.localeCompare(b.text));
        setCompanyContentType(resData);
        let subContent = [];
        resData?.forEach((content) => {
          subContent = [...subContent, ...content.subContentType];
        });
        const subContentType = subContent
          ?.map((v) => {
            return { id: v.contentType, text: v.contentType };
          })
          .filter((val) => val !== undefined);
        subContentType.sort((a, b) => a.text.localeCompare(b.text));
        setCompanySubContentType(subContentType);
      }
    }
  };

  const { data: deliveryTypeData } = useQuery({
    queryKey: ["delivery-type-config"],
    queryFn: () => {
      let params = {};
      params.filters = [["configName", "eq", "delivery_type"]];
      return fetchConfig(params);
    },
    suspense: false,
    enabled: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 1,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (deliveryTypeData?.data?.data?.[0]?.configValue) {
      let deliveryData = deliveryTypeData.data.data[0].configValue;

      const deliveryType = deliveryData.map((ele) => ({
        id: ele,
        text: ele,
      }));

      setCompanyDeliveryType(deliveryType);
    }
  }, [deliveryTypeData]);

  // Reset Redux
  useEffect(() => {
    const locationPath = localStorage.getItem("locationPath") ? localStorage.getItem("locationPath") : [];
    if (locationPath != `/company`) {
      dispatch(resetRedux());
      dispatch(setFilteredTaxonomy([]));
      setFilterData([]);
      localStorage.setItem("locationPath", window.location.pathname);
    }
    fetchContentType();
  }, []);

  /* Common access */
  const accessActionEdit = props?.interfaceActionAccess?.includes(COMMON_ACCESS_ACTION.EDIT);
  const accessActionDelete = props?.interfaceActionAccess?.includes(COMMON_ACCESS_ACTION.DELETE);
  const accessActionImport = props?.interfaceActionAccess?.includes(COMMON_ACCESS_ACTION.IMPORT);
  const accessActionExport = props?.interfaceActionAccess?.includes(COMMON_ACCESS_ACTION.EXPORT);
  const accessActionListingAddNew = props?.interfaceActionAccess?.includes(COMMON_ACCESS_ACTION.ADD_NEW);
  const accessActionFilterByTaxonomy = props?.interfaceActionAccess?.includes(COMMON_ACCESS_ACTION.FILTER_BY_TAXONOMY);
  const accessActionTaxonomyBulkTagging = props?.interfaceActionAccess?.includes(COMMON_ACCESS_ACTION.TAXONOMY_BULK_TAGGING);

  // specify column names for column fields in datGrid here
  const columnNames = {
    id: "id",
    Name: "companyName",
    Website: "companyWebsite",
    // Technology: "companyTechnologyTags~dtParentId:0,dtName",
    "Country (HQ)": "companyLocation~type:Headquarter,countryName",
    Founded: "companyFoundedYear",
    "Employee Size": "companyEmpSize",
    Startup: "companyIsStartup",
    // "Is Live": "companyIsLive",
    "Queue Status": "companyInQueue.queueStatus",
    WIP: "companyWorkInProgress",
    publishDate: "productVariant.lastPublishedDate",
    companyCreatedDate: "companyCreatedDate",
    companyUpdatedDate: "companyUpdatedDate",
    "Business Impact Comments": "companyBusinessImpactComments",
  };
  if (productVariant) columnNames["Is Live"] = `productVariant~productName:${productVariant},isLive`;
  else columnNames["Is Live"] = "productVariant.isLive";

  if (productVariant && productVariant === "Insider") columnNames["Trend"] = "companyInQueue.trendId";
  else columnNames["Technology"] = "companyInQueue.dtId";

  if (productVariant && productVariant === PRODUCT_VARIANT.INSIDER) {
    columnNames["Content Type"] = "companyContentType";
    columnNames["Sub Content Type"] = "companySubContentType";
    columnNames["Delivery Type"] = "companyDeliveryType";
  }
  const companyNameTemplate = (value) => {
    return (
      <span
        className="span-link"
        onClick={() => {
          history.push(`/company/${value.id}/edit#basic`, { companyId: value.id, companyName: value.Name });
          dispatch(visitRecord());
        }}
      >
        {value.Name}
      </span>
    );
  };

  /* 
        @Description : Template Component for action column
    */
  const actionTemplate = (value) => {
    return (
      <ThreeDotMenu
        rowID={value.id}
        {...(accessActionEdit
          ? {
              Edit: () => {
                history.push(`/company/${value.id}/edit#basic`, { companyId: value.id, companyName: value.Name });
                dispatch(visitRecord());
              },
            }
          : {})}
        {...(accessActionDelete
          ? {
              Delete: () => {
                dispatch(
                  showAlertBox({
                    okCallback: async () => {
                      deleteCompany(value.id)
                        .then((response) => {
                          if (deleteId) setDeleteId(undefined);
                          setDeleteId(true);
                          dispatch(actionSuccess("Company deleted successfully"));
                          const params = {};
                          params.limit = 100;
                          if (productVariant) params.filters = [["productVariant.productName", "eq", productVariant]];
                          fetchCompany(params).then((company) => {
                            formatRowData(company.data.data);
                          });
                        })
                        .catch((err) => {
                          dispatch(actionError(err.data?.message || "Failed to delete company"));
                        });
                    },
                    okText: "Delete",
                    cancelText: "Cancel",
                    content: "Are you sure you want to delete ?",
                    title: "dialogAlertCssWarning",
                  })
                );
              },
            }
          : {})}
      />
    );
  };
  const companyWebsiteTemplate = (value) => {
    return (
      <a
        href={value.Website ? (value.Website.startsWith("http") ? value.Website : "http://" + value.Website) : null}
        target="_blank"
        rel="noopener noreferrer"
        data-tip="Open Website"
        style={{
          color: "inherit",
          textDecoration: "none",
          transition: "all 0.3s",
        }}
      >
        {value.Website}
        {value.Website ? <i className="fas fa-external-link-alt pl-2"></i> : null}
      </a>
    );
  };

  // Dropdown Filter template
  const ItemFilter = (value) => {
    let sourceData = [];
    switch (value.column.field) {
      case "Startup":
        sourceData = [
          { id: "-", text: "Blanks" },
          { id: "YES", text: "YES" },
          { id: "NO", text: "NO" },
        ];
        break;
      case "WIP":
        sourceData = [
          { id: " ", text: "Blanks" },
          { id: "YES", text: "YES" },
          { id: "NO", text: "NO" },
        ];
        break;
      case "Is Live":
        sourceData = [
          { id: "YES", text: "YES" },
          { id: "NO", text: "NO" },
        ];
        break;
      case "Queue Status":
        sourceData = Object.keys(QUEUE_STATUS)
          .map((key) => {
            if (!["SCREENING_FRESH", "SCREENING_REJECTED", "SCREENING_HOLD"].includes(key)) {
              return { id: QUEUE_STATUS[key], text: QUEUE_STATUS[key] };
            }
          })
          .filter((item) => {
            return item !== undefined;
          });
        break;
      case "Employee Size":
        sourceData = [
          { id: "-", text: "Blanks" },
          { id: "1 - 10", text: "1 to 10" },
          { id: "11 - 50", text: "11 to 50" },
          { id: "51 - 100", text: "51 to 100" },
          { id: "101 - 500", text: "101 to 500" },
          { id: "501 - 1000", text: "501 to 1000" },
          { id: "1001 - 5000", text: "1001 to 5000" },
          { id: "5001 - 10000", text: "5001 to 10000" },
          { id: "10000+", text: "10000+" },
        ];
        break;
      case "Technology":
        sourceData = techTrendData;
        break;
      case "Trend":
        sourceData = techTrendData;
        break;
      case "Content Type":
        sourceData = companyContentType;
        break;
      case "Sub Content Type":
        sourceData = companySubContentType;
        break;
      case "Delivery Type":
        sourceData = companyDeliveryType;
        break;
      default:
        break;
    }

    return <DropDownFilter value={value} sourceData={sourceData} />;
  };

  /* 
        @Description : Template Component for Date col
    */
  const LastPublishedDateTemplate = (value) => {
    const column = value.column.field;
    return ["companyCreatedDate", "companyUpdatedDate", "publishDate"].includes(column) && !isNaN(Date.parse(value[`${column}`])) ? (
      <span>{getDateFromMongoDate(new Date(value[`${column}`]))}</span>
    ) : (
      ""
    );
  };

  // specify column fields for datGrid here
  // 4 types of field - String, Numeric, Date, Boolean
  const columnFields = [
    { field: "id", type: "String", visible: false, allowFiltering: false, showInColumnChooser: false, isPrimaryKey: true },
    {
      field: "Name",
      type: "String",
      template: companyNameTemplate,
      filterTemplate: StringFilter,
      textAlign: "Left",
      headerTextAlign: "Left",
      showInColumnChooser: false,
      width: 200,
    },
    {
      field: "Website",
      type: "String",
      template: companyWebsiteTemplate,
      filterTemplate: StringFilter,
      textAlign: "Left",
      headerTextAlign: "Left",
      width: 200,
    },
    {
      field: `${productVariant && productVariant === "Insider" ? "Trend" : "Technology"}`,
      type: "String",
      filter: { operator: "contains" },
      filterTemplate: ItemFilter,
      textAlign: "Left",
      headerTextAlign: "Left",
      width: 230,
    },
    productVariant &&
      productVariant === PRODUCT_VARIANT.INSIDER && {
        field: "Content Type",
        type: "String",
        filterTemplate: ItemFilter,
        filter: { operator: "equal" },
        textAlign: "Left",
        width: 280,
      },
    productVariant &&
      productVariant === PRODUCT_VARIANT.INSIDER && {
        field: "Sub Content Type",
        type: "String",
        filterTemplate: ItemFilter,
        filter: { operator: "equal" },
        textAlign: "Left",
        width: 190,
      },
    productVariant &&
      productVariant === PRODUCT_VARIANT.INSIDER && {
        field: "Delivery Type",
        type: "String",
        filterTemplate: ItemFilter,
        filter: { operator: "equal" },
        textAlign: "Left",
        width: 190,
      },
    { field: "Country (HQ)", type: "String", filterTemplate: StringFilter, textAlign: "Left", headerTextAlign: "Left", width: 150 },
    { field: "Founded", type: "String", filterTemplate: StringFilter, textAlign: "Center", headerTextAlign: "Center", width: 140 },
    {
      field: "Employee Size",
      type: "String",
      filterTemplate: StringFilter,
      textAlign: "Left",
      filter: { operator: "equal" },
      headerTextAlign: "Left",
      width: 160,
    },
    {
      field: "Startup",
      type: "String",
      filterTemplate: ItemFilter,
      filter: { operator: "equal" },
      textAlign: "Center",
      headerTextAlign: "Center",
      width: 130,
    },
    {
      field: "Is Live",
      type: "String",
      filterTemplate: ItemFilter,
      filter: { operator: "equal" },
      textAlign: "Center",
      headerTextAlign: "Center",
      width: 140,
    },
    {
      field: "companyCreatedDate",
      headerText: "Created Date",
      type: "Date",
      template: LastPublishedDateTemplate,
      width: 160,
      headerTextAlign: "Center",
      textAlign: "Center",
      useRange: true,
    },
    {
      field: "companyUpdatedDate",
      headerText: "Last Updated Date",
      type: "Date",
      template: LastPublishedDateTemplate,
      width: 200,
      headerTextAlign: "Center",
      textAlign: "Center",
      useRange: true,
    },
    {
      field: "publishDate",
      headerText: "Published Date",
      type: "Date",
      template: LastPublishedDateTemplate,
      width: 220,
      headerTextAlign: "Center",
      textAlign: "Center",
      useRange: true,
    },
    {
      field: "Queue Status",
      type: "String",
      filterTemplate: ItemFilter,
      filter: { operator: "equal" },
      textAlign: "Left",
      headerTextAlign: "Left",
      width: 150,
    },
    {
      field: "WIP",
      type: "String",
      filterTemplate: ItemFilter,
      filter: { operator: "equal" },
      textAlign: "Center",
      headerTextAlign: "Center",
      width: 105,
    },
    {
      field: "Business Impact Comments",
      type: "String",
      filterTemplate: StringFilter,
      textAlign: "Left",
      headerTextAlign: "Left",
      width: 250,
    },
  ];
  if (accessActionEdit || accessActionDelete) {
    columnFields.unshift({ field: null, type: "checkbox", width: 32, allowFiltering: false, showInColumnChooser: false, textAlign: "Center" });
    columnFields.push({
      field: "Action",
      type: "String",
      template: actionTemplate,
      allowSorting: false,
      allowFiltering: false,
      textAlign: "Right",
      headerTextAlign: "Center",
      width: 65,
      showInColumnChooser: false,
      freeze: "Right",
    });
  }

  //@Description formatRowData function required to format rowData for datGrid
  const formatRowData = (rowData) => {
    let formatedRowData = [];
    const columnNamesValues = swap(columnNames);
    formatedRowData = rowData.map((rD) => {
      const newRD = { Id: rD.id };
      Object.keys(rD).forEach((rDK) => {
        if (columnNamesValues[rDK]) {
          newRD[`${columnNamesValues[rDK]}`] = rD[rDK] && rD[rDK] !== "" ? rD[rDK] : "";
        }
      });
      if (productVariant && productVariant === "Insider") {
        if (rD.companyInQueue && rD.companyInQueue.trendId && rD.companyInQueue.trendId.length) {
          const ids = rD.companyInQueue.trendId;

          const trendData = techTrendData.filter((item) => {
            return ids.includes(item.id);
          });
          newRD.Trend = trendData
            .map((item) => {
              return item.text;
            })
            .filter((trend, index, self) => trend !== "" && self.indexOf(trend) === index)
            .join(", ");
        } else newRD.Trend = [];
        newRD["Content Type"] = rD?.companyContentType || "";
        newRD["Sub Content Type"] = rD?.companySubContentType || "";
        newRD["Delivery Type"] = rD?.companyDeliveryType || "";
      } else {
        if (rD.companyInQueue && rD.companyInQueue.dtId && rD.companyInQueue.dtId.length) {
          const ids = rD.companyInQueue.dtId;

          const techData = techTrendData.filter((item) => {
            return ids.includes(item.id);
          });
          newRD.Technology = techData
            .map((item) => {
              return item.text;
            })
            .filter((tech, index, self) => tech !== "" && self.indexOf(tech) === index)
            .join(", ");
        } else {
          newRD.Technology = [];
        }
      }
      if (rD.companyLocation && rD.companyLocation.length) {
        const hq = rD.companyLocation.filter((nD) => nD.type === "Headquarter");
        newRD["Country (HQ)"] = hq.length ? hq[0].countryName : "";
      } else {
        newRD["Country (HQ)"] = "";
      }
      if (rD.companyInQueue && rD.companyInQueue.queueStatus) {
        newRD["Queue Status"] = QUEUE_STATUS[rD.companyInQueue.queueStatus];
      } else {
        newRD["Queue Status"] = "";
      }
      if (rD.productVariant?.length) {
        if (productVariant) {
          const rDPV = rD.productVariant.find((pV) => pV.productName === productVariant);
          newRD["Is Live"] = rDPV?.isLive ? rDPV.isLive : "NO";
          newRD["publishDate"] = rDPV?.lastPublishedDate ? new Date(rDPV.lastPublishedDate) : "";
        } else {
          newRD["Is Live"] = rD.productVariant[0].isLive ? rD.productVariant[0].isLive : "NO";
          newRD["publishDate"] = rD.productVariant[0].lastPublishedDate ? new Date(rD.productVariant[0].lastPublishedDate) : "";
        }
      } else {
        newRD["Is Live"] = "NO";
        newRD["publishDate"] = "";
      }
      if (rD.companyWorkInProgress === undefined) newRD["WIP"] = " ";
      newRD["companyCreatedDate"] = (rD.companyCreatedDate && new Date(rD.companyCreatedDate)) || "";
      newRD["companyUpdatedDate"] = (rD.companyUpdatedDate && new Date(rD.companyUpdatedDate)) || "";
      if (rD?.companyBusinessImpactComments === undefined) newRD["Business Impact Comments"] = " ";
      return newRD;
    });

    return formatedRowData;
  };
  const fetchTechData = async () => {
    setTechTrendData(await fetchTechTrendData(productVariant));
  };

  // const dispatch = useDispatch();
  // useEffect(() => {

  // }, [formatRowData])
  useEffect(() => {
    localStorage.removeItem("selectedCompanies");
    if (!techTrendData) fetchTechData();
    dispatch(setBreadCrumb(breadCrumbLinks));
    return () => {
      dispatch(setBreadCrumb());
      localStorage.removeItem("selectedCompanies");
    };
  }, [dispatch]);

  const fetchCompanyWithProductVariant = (params = {}) => {
    dispatch(resetInternalGrid());
    let defaultFilter = [];
    let { filters = [] } = params;
    if (!gridState.recordVisited) {
      if (!params.skip) {
        params.skip = 0;
      }
      dispatch(setMaintainedState(params.skip, params.filters, params.sort));
    }
    if (gridState.recordVisited) {
      params = {
        filters: gridState.filtersParams,
        sort: gridState.sortRecords,
        skip: gridState.skipRecords,
        limit: gridState.recordsPerPage,
      };
      dispatch(setMaintainedState(params.skip, params.filters, params.sort));
    }
    if (productVariant) defaultFilter.push(["productVariant.productName", "eq", productVariant]);
    let trendFiltering = false;
    if (params.filters) {
      filters = params.filters.map((item) => {
        /**True-false is trend filter is selected */
        if (item.includes("companyInQueue.trendId")) trendFiltering = item.includes("companyInQueue.trendId");
        /** */
        const columnName = swap(QUEUE_STATUS);
        const tech = techTrendData.filter((f) => {
          return ["companyInQueue.trendId", "companyInQueue.dtId"].includes(item[0]) && f.text === item[2];
        });

        if (productVariant === "Insider") {
          return item[0] === "companyInQueue.queueStatus"
            ? [item[0], item[1], columnName[`${item[2]}`]]
            : item[0] === "companyInQueue.trendId"
            ? [item[0], "in", [tech[0].id]]
            : item;
        } else {
          return item[0] === "companyInQueue.queueStatus"
            ? [item[0], item[1], columnName[`${item[2]}`]]
            : item[0] === "companyInQueue.dtId"
            ? [item[0], "in", [tech[0].id]]
            : item;
        }
      });
    }

    let userCompetencyIndustry = localStorage.getItem("userCompetencyIndustry") !== "null" ? localStorage.getItem("userCompetencyIndustry") : "null";
    if (!trendFiltering && userCompetencyIndustry !== "null" && userCompetencyIndustry !== undefined) {
      userCompetencyIndustry = JSON.parse(userCompetencyIndustry);
      const industryId = userCompetencyIndustry?.map((obj) => obj.id);
      productVariant === PRODUCT_VARIANT.INSIDER &&
        userCompetencyIndustry?.length > 0 &&
        defaultFilter.push(["companyIndustryTrendsTags.trendId", "in", industryId]);
    }
    params.filters = [...filters, ...defaultFilter];
    if (!params.sort) {
      params.sort = "companyCreatedDate:desc";
    }
    setGridFilters(JSON.stringify(params.filters));
    localStorage.removeItem("selectedCompanies");
    if (params.filters?.length === 0) {
      params.filters = [...filters, ...defaultFilter];
      return fetchCompany(params);
    }
    if (Object.keys(filterData).length > 0) {
      let technologyFilter = [];
      let kiaFilter = [];
      let valueChainFilter = [];
      let businessObjective = [];
      technologyFilter = filterData?.technology?.length > 0 ? ["companyTechnologyTags.dtId", "in", filterData.technology] : [];
      kiaFilter = filterData?.kia?.length > 0 ? ["companyKiaTags.kiaId", "in", filterData.kia] : [];
      valueChainFilter = filterData?.valueChain?.length > 0 ? ["companyIndustryValuechainTags.valueChainId", "in", filterData.valueChain] : [];
      let boTaxonomy = filterData?.boTaxonomy?.length > 0 ? ["companyObjectiveTaxonomy.taxonomyId", "in", filterData.boTaxonomy] : [];
      businessObjective =
        filterData?.businessObjective?.length > 0 ? ["companyBusinessObjective.objectiveId", "in", filterData.businessObjective] : [];
      let trendfilter = filterData?.trend?.length > 0 ? ["companyIndustryTrendsTags.trendId", "in", filterData.trend] : [];
      params.filters = [...filters, ...defaultFilter];
      technologyFilter.length > 0 && params.filters.push(technologyFilter);
      kiaFilter.length > 0 && params.filters.push(kiaFilter);
      valueChainFilter.length > 0 && params.filters.push(valueChainFilter);
      businessObjective.length > 0 && params.filters.push(businessObjective);
      boTaxonomy.length > 0 && params.filters.push(boTaxonomy);
      trendfilter.length > 0 && params.filters.push(trendfilter);
    }

    return fetchCompany(params);
  };

  const deleteManyFunc = (flag) => {
    const selectedCompanies = localStorage.getItem("selectedCompanies") ? JSON.parse(localStorage.getItem("selectedCompanies")) : [];
    if (flag) {
      if (selectedCompanies.length) {
        dispatch(
          showAlertBox({
            okCallback: () => {
              deleteManyCompany({ companyIds: selectedCompanies })
                .then((response) => {
                  if (response) {
                    if (deleteId) setDeleteId(undefined);
                    setDeleteId(true);
                    dispatch(actionSuccess("Selected record(s) deleted successfully"));
                    const params = {};
                    params.limit = 100;
                    if (productVariant) params.filters = [["productVariant.productName", "eq", productVariant]];
                    fetchCompany(params).then((company) => {
                      formatRowData(company.data.data);
                    });
                  }
                })
                .catch((err) => {
                  dispatch(actionError(err.data?.message || "Failed to delete company"));
                });
            },
            content: "Are you sure, you want to delete records?",
            okText: "Delete",
            cancelText: "Cancel",
            title: "dialogAlertCssWarning",
          })
        );
      } else {
        dispatch(actionError("No record selected for deletion"));
      }
    }
  };

  // Export Company data
  const exportData = async (flag, ids) => {
    if (flag) {
      const payload = {
        reqIds: JSON.stringify(ids),
        productVariant: productVariant,
      };
      if (gridFilters) payload.filters = gridFilters;
      dispatch(showLoader());
      await exportJsonData(payload)
        .then((res) => {
          const link = document.createElement("a");
          link.href = downloadFileUrl(`${res.data.data}`);
          link.download = `${res.data.data}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          dispatch(hideLoader());
        })
        .catch((err) => {
          dispatch(actionError("Something Went Wrong...!"));
          console.error("Error: ", err);
        });
    }
  };

  const objectiveTaxonomyTag = async () => {
    const selectedCompanies = localStorage.getItem("selectedCompanies") ? JSON.parse(localStorage.getItem("selectedCompanies")) : [];
    dispatch(visitRecord());
    if (selectedCompanies.length) {
      const params = {};
      params.filters = [
        ["productVariant.productName", "eq", productVariant],
        ["_id", "in", selectedCompanies],
      ];
      params.fields = ["productVariant"];
      dispatch(showLoader());
      const companyIsLive = await fetchCompany(params);
      dispatch(hideLoader());
      if (companyIsLive.data) {
        let nonLiveComp = companyIsLive.data.data.filter((comp) => {
          let isLIveComp = true;
          comp.productVariant.forEach((prod) => {
            if (prod.productName === productVariant && prod.isLive === "YES") {
              isLIveComp = false;
            }
          });
          return isLIveComp;
        });
        if (nonLiveComp.length) {
          dispatch(actionError("Please select only live company."));
        } else {
          history.push(`/business-objective/taxonomy/company`, { companyIds: selectedCompanies, entityName: "company" });
        }
      }
    } else {
      dispatch(actionError("Please select company."));
    }
  };

  /* 
    @Description : Bulk tagging technology tab
    */
  const handleSubmitTechnology = async (fetchGridData) => {
    let payload = {
      companyIds: initalState,
      taxonomyTagging: companyTaxonomy.taxonomyCheckedNodes,
      taxonomyUnTagging: companyTaxonomy.taxonomyUnCheckedNodes,
    };

    if (companyTaxonomy?.initialTechnologyCheckedNodes.length == 0) {
      try {
        dispatch(
          showAlertBox({
            okCallback: async () => {
              setTimeout(() => {
                dispatch(hideLoader());
              }, 0);
            },
            cancelCallback: async () => {
              setShowTaxonomyBulk(false);
            },

            okText: "Ok",
            cancelText: "Cancel",
            content: "There is no change observed in taxonomy tagging, press Ok to continue or press Cancel to close.",
            title: "dialogAlertCssWarning",
          })
        );
      } catch (error) {
        dispatch(actionError("Something Went Wrong...!"));
      }
    } else {
      try {
        dispatch(
          showAlertBox({
            okCallback: async () => {
              setTimeout(() => {
                dispatch(showLoader());
                saveCompanyTaxonomyTags(payload)
                  .then((result) => {
                    dispatch(actionSuccess("Taxonomy updated successfully"));
                    dispatch(setResetRedux());
                    dispatch(hideLoader());
                    setShowTaxonomyBulk(false);
                    fetchGridData();
                  })
                  .catch((err) => {
                    dispatch(hideLoader());
                    dispatch(
                      showAlertBox({
                        okCallback: async () => {
                          setTimeout(() => {
                            dispatch(hideLoader());
                            setShowTaxonomyBulk(false);
                            dispatch(setResetRedux());
                          }, 0);
                        },
                        okText: "Ok",
                        hideCancelButton: true,
                        content:
                          "Some of the selected companies has products, taxonomy can not be untagged from such companies. You need to untagged taxonomy from their products.",
                        title: "dialogAlertCssWarning",
                      })
                    );
                  });
              }, 0);
            },
            okText: "Submit",
            cancelText: "Cancel",
            content: "Are you sure you want to update the tagging?",
            title: "dialogAlertCssWarning",
          })
        );
      } catch (error) {
        dispatch(actionError("Something went wrong...!"));
      }
    }
  };

  const fetchTechnologyTagging = () => {
    setListClass("select-lists bulktagging");
    setCssClass("treeClass");
    const selectedCompanies = localStorage.getItem("selectedCompanies") ? JSON.parse(localStorage.getItem("selectedCompanies")) : [];
    if (selectedCompanies.length) {
      setBulkCheck([]);
      const params = { limit: gridState.recordsPerPage };
      params.filters = [
        ["productVariant.productName", "eq", productVariant],
        ["_id", "in", selectedCompanies],
      ];
      params.fields = ["companyTechnologyTags"];
      const contentTaxonomy = fetchCompany(params);
      contentTaxonomy
        .then((res) => {
          let checkNodes = [];
          let count;
          res.data.data.map((value) => {
            value.companyTechnologyTags.length > 0 &&
              value.companyTechnologyTags.map((v) => {
                checkNodes.push(v.dtId);
                count = {};
                for (const element of checkNodes) {
                  if (count[element]) {
                    count[element] += 1;
                  } else {
                    count[element] = 1;
                  }
                }
              });
            let checked = Object.entries(count);
            let commonIds = [];
            checked.map((d) => {
              if (selectedCompanies.length == d[1]) {
                commonIds.push(d[0]);
              }
            });
            setBulkCheck([...companyTaxonomy.taxonomyCheckedNodes, ...commonIds]);
            setTaxonomyState([...commonIds]);
            dispatch(setTaxonomyCheckedNodes([...companyTaxonomy.taxonomyCheckedNodes, ...commonIds]));
          });
        })
        .catch((err) => {
          console.log("err fetchProductTaxonomy :: ", err);
        });
    }
  };

  const fetchValuchainData = (data, selectedCompanies) => {
    if (data.length) {
      let valueChainIds = [];
      let commanIds = [];
      let taxonomyTee = [];
      data.map((value) => {
        value.companyIndustryTag.length > 0 &&
          value.companyIndustryTag.map((item) => {
            if (!valueChainIds.includes(item.industryId)) {
              let taxonomyTeeData = {};
              taxonomyTeeData["id"] = item.industryId;
              taxonomyTeeData["name"] = item.industryName;
              taxonomyTee.push(taxonomyTeeData);
            }
            valueChainIds.push(item.industryId);
          });
      });
      let count = _.countBy(valueChainIds);
      let countarray = Object.entries(count);
      countarray.map((d) => {
        if (selectedCompanies.length == d[1]) {
          commanIds.push(d[0]);
        }
      });
      let valueChainData = _.filter(taxonomyTee, function (element) {
        return _.find(commanIds, function (name) {
          return element.id === name;
        });
      });
      setValueChainData(valueChainData);
    }
  };

  const taxonomyBulkTagging = async () => {
    dispatch(visitRecord());
    const selectedCompanies = localStorage.getItem("selectedCompanies") ? JSON.parse(localStorage.getItem("selectedCompanies")) : [];
    if (selectedCompanies.length) {
      const params = {};
      params.filters = [
        ["productVariant.productName", "eq", productVariant],
        ["_id", "in", selectedCompanies],
      ];
      params.fields = ["companyIndustryTag"];
      const companyIsLive = await fetchCompany(params);
      dispatch(hideLoader());
      if (companyIsLive) {
        dispatch(showLoader());
        setInitialState(selectedCompanies);
        setShowTaxonomyBulk(true);
        companyIsLive?.data?.data && fetchValuchainData(companyIsLive.data.data, selectedCompanies);
      }
    } else {
      dispatch(actionError("Please select company."));
    }
  };

  const addTaxonomyCheckedNodes = (id) => {
    let taxonomyCheck = companyTaxonomy.taxonomyCheckedNodes;
    let taxonomyUnCheck = companyTaxonomy.taxonomyUnCheckedNodes;

    // for initial nodes
    let initialCheckedNodes = companyTaxonomy.initialTechnologyCheckedNodes;
    if (initialCheckedNodes.includes(id)) {
      initialCheckedNodes.splice(initialCheckedNodes.indexOf(id), 1);
      dispatch(setInitialCheckedTechnologyNodes(initialCheckedNodes));
    }
    dispatch(setInitialCheckedTechnologyNodes([...companyTaxonomy.initialTechnologyCheckedNodes, id]));

    if (taxonomyCheck.includes(id)) {
      taxonomyCheck.splice(taxonomyCheck.indexOf(id), 1);
      dispatch(setTaxonomyCheckedNodes(taxonomyCheck));
      if (bulkCheck.includes(id)) {
        dispatch(setTaxonomyUnCheckedNodes([...companyTaxonomy.taxonomyUnCheckedNodes, id]));
      }
      return;
    }
    if (taxonomyUnCheck.includes(id)) {
      taxonomyUnCheck.splice(taxonomyUnCheck.indexOf(id), 1);
      dispatch(setTaxonomyUnCheckedNodes(taxonomyUnCheck));
    }
    dispatch(setTaxonomyCheckedNodes([...companyTaxonomy.taxonomyCheckedNodes, id]));
  };

  /* 
        @Description : Bulk tagging for kia
    */
  const handleSubmitKia = async (fetchGridData) => {
    let payload = {
      companyIds: initalState,
      taxonomyTagging: companyTaxonomy.kiaCheckedNodes,
      taxonomyUnTagging: companyTaxonomy.kiaUnCheckedNodes,
    };

    if (companyTaxonomy?.initialKiaCheckedNodes.length == 0) {
      try {
        dispatch(
          showAlertBox({
            okCallback: async () => {
              setTimeout(() => {
                dispatch(hideLoader());
              }, 0);
            },
            cancelCallback: async () => {
              setShowTaxonomyBulk(false);
            },

            okText: "Ok",
            cancelText: "Cancel",
            content: "There is no change observed in taxonomy tagging, press Ok to continue or press Cancel to close.",
            title: "dialogAlertCssWarning",
          })
        );
      } catch (error) {
        dispatch(actionError("Something Went Wrong...!"));
      }
    } else {
      try {
        dispatch(
          showAlertBox({
            okCallback: () => {
              setTimeout(() => {
                dispatch(showLoader());
                saveCompanyKiaTags(payload)
                  .then((result) => {
                    dispatch(actionSuccess("Taxonomy updated successfully"));
                    dispatch(setResetRedux());
                    setShowTaxonomyBulk(false);
                    dispatch(hideLoader());
                    fetchGridData();
                  })
                  .catch((err) => {
                    dispatch(hideLoader());
                    dispatch(
                      showAlertBox({
                        okCallback: async () => {
                          setTimeout(() => {
                            dispatch(hideLoader());
                            setShowTaxonomyBulk(false);
                            dispatch(setResetRedux());
                          }, 0);
                        },
                        okText: "Ok",
                        hideCancelButton: true,
                        content:
                          "Some of the selected companies has products, taxonomy can not be untagged from such companies. You need to untagged taxonomy from their products.",
                        title: "dialogAlertCssWarning",
                      })
                    );
                  });
              }, 0);
            },
            okText: "Submit",
            cancelText: "Cancel",
            content: "Are you sure you want to update the tagging?",
            title: "dialogAlertCssWarning",
          })
        );
      } catch (error) {
        dispatch(actionError("Something went wrong...!"));
      }
    }
  };

  const fetchKiaTagging = () => {
    setListClass("select-lists bulktagging");
    setCssClass("treeClass");
    const selectedCompanies = localStorage.getItem("selectedCompanies") ? JSON.parse(localStorage.getItem("selectedCompanies")) : [];
    if (selectedCompanies.length) {
      setBulkCheck([]);
      const params = { limit: gridState.recordsPerPage };
      params.filters = [
        ["productVariant.productName", "eq", productVariant],
        ["_id", "in", selectedCompanies],
      ];
      params.fields = ["companyKiaTags"];
      const contentTaxonomy = fetchCompany(params);
      contentTaxonomy
        .then((res) => {
          let checkNodes = [];
          let count;
          res.data.data.map((value) => {
            value.companyKiaTags.length > 0 &&
              value.companyKiaTags.map((v) => {
                checkNodes.push(v.kiaId);
                count = {};
                for (const element of checkNodes) {
                  if (count[element]) {
                    count[element] += 1;
                  } else {
                    count[element] = 1;
                  }
                }
              });

            let checked = Object.entries(count);
            let commonIds = [];
            checked.map((d) => {
              if (selectedCompanies.length == d[1]) {
                commonIds.push(d[0]);
              }
            });

            commonIds.map((element) => {
              value.companyKiaTags.length > 0 &&
                value.companyKiaTags.map((v) => {
                  if (v.kiaId === element) {
                    commonIds.push(v.kiaIndustryId);
                  }
                });
            });
            commonIds = [...new Set(commonIds)];
            setBulkCheck([...companyTaxonomy.kiaCheckedNodes, ...commonIds]);
            setTaxonomyState([...commonIds]);
            dispatch(setKiaCheckedNodes([...companyTaxonomy.kiaCheckedNodes, ...commonIds]));
          });
        })
        .catch((err) => {
          console.log("err fetchProductTaxonomy :: ", err);
        });
    }
  };

  const addKiaCheckedNodes = (id) => {
    let taxonomyCheck = companyTaxonomy.kiaCheckedNodes;
    let taxonomyUnCheck = companyTaxonomy.kiaUnCheckedNodes;

    // for initial nodes
    let initialCheckedNodes = companyTaxonomy.initialKiaCheckedNodes;
    if (initialCheckedNodes.includes(id)) {
      initialCheckedNodes.splice(initialCheckedNodes.indexOf(id), 1);
      dispatch(setInitialCheckedKiaNodes(initialCheckedNodes));
    }
    dispatch(setInitialCheckedKiaNodes([...companyTaxonomy.initialKiaCheckedNodes, id]));

    if (taxonomyCheck.includes(id)) {
      taxonomyCheck.splice(taxonomyCheck.indexOf(id), 1);
      dispatch(setKiaCheckedNodes(taxonomyCheck));
      if (bulkCheck.includes(id)) {
        dispatch(setKiaUnCheckedNodes([...companyTaxonomy.kiaUnCheckedNodes, id]));
      }
      return;
    }
    if (taxonomyUnCheck.includes(id)) {
      taxonomyUnCheck.splice(taxonomyUnCheck.indexOf(id), 1);
      dispatch(setKiaUnCheckedNodes(taxonomyUnCheck));
    }
    dispatch(setKiaCheckedNodes([...companyTaxonomy.kiaCheckedNodes, id]));
  };
  /* 
        @Description : Bulk tagging Value chain
    */
  const handleSubmitValueChain = async (fetchGridData) => {
    let payload = {
      companyIds: initalState,
      taxonomyTagging: companyTaxonomy.valueChainCheckedNodes,
      taxonomyUnTagging: companyTaxonomy.valueChainUnCheckedNodes,
      transformingTechnologiesIds: companyTaxonomy.transformingCheckedNodes,
      transformingUnCheckedTechnologiesIds: companyTaxonomy.transformingUnCheckedNodes,
    };

    if (companyTaxonomy?.initialValueChainCheckedNodes.length == 0) {
      try {
        dispatch(
          showAlertBox({
            okCallback: async () => {
              setTimeout(() => {
                dispatch(hideLoader());
              }, 0);
            },
            cancelCallback: async () => {
              setShowTaxonomyBulk(false);
            },

            okText: "Ok",
            cancelText: "Cancel",
            content: "There is no change observed in taxonomy tagging, press Ok to continue or press Cancel to close.",
            title: "dialogAlertCssWarning",
          })
        );
      } catch (error) {
        dispatch(actionError("Something Went Wrong...!"));
      }
    } else {
      try {
        dispatch(
          showAlertBox({
            okCallback: () => {
              setTimeout(() => {
                dispatch(showLoader());
                saveValueChainTags(payload)
                  .then((result) => {
                    dispatch(actionSuccess("Taxonomy updated successfully"));
                    dispatch(setResetRedux());
                    setShowTaxonomyBulk(false);
                    dispatch(hideLoader());
                    fetchGridData();
                  })
                  .catch((err) => {
                    dispatch(hideLoader());
                    dispatch(
                      showAlertBox({
                        okCallback: async () => {
                          setTimeout(() => {
                            dispatch(hideLoader());
                            setShowTaxonomyBulk(false);
                            dispatch(setResetRedux());
                          }, 0);
                        },
                        okText: "Ok",
                        hideCancelButton: true,
                        content:
                          "Some of the selected companies has products, taxonomy can not be untagged from such companies. You need to untagged taxonomy from their products.",
                        title: "dialogAlertCssWarning",
                      })
                    );
                  });
              }, 0);
            },
            okText: "Submit",
            cancelText: "Cancel",
            content: "Are you sure you want to update the tagging?",
            title: "dialogAlertCssWarning",
          })
        );
      } catch (error) {
        dispatch(actionError("Something went wrong...!"));
      }
    }
  };

  const fetchValueChainTagging = () => {
    setListClass("select-lists bulktagging");
    setCssClass("treeClass");
    const selectedCompanies = localStorage.getItem("selectedCompanies") ? JSON.parse(localStorage.getItem("selectedCompanies")) : [];
    if (selectedCompanies.length) {
      setBulkCheck([]);
      const params = { limit: gridState.recordsPerPage };
      params.filters = [
        ["productVariant.productName", "eq", productVariant],
        ["_id", "in", selectedCompanies],
      ];
      params.fields = ["companyIndustryValuechainTags"];
      const contentTaxonomy = fetchCompany(params);
      contentTaxonomy
        .then((res) => {
          let checkNodes = [];
          let checkNodes2 = [];
          let count;
          let count2;
          res.data.data.map((value) => {
            value.companyIndustryValuechainTags.length > 0 &&
              value.companyIndustryValuechainTags.map((v) => {
                checkNodes.push(v.valueChainId);
                count = {};
                for (const element of checkNodes) {
                  if (count[element]) {
                    count[element] += 1;
                  } else {
                    count[element] = 1;
                  }
                }

                // transformingTechnologies id
                v.transformingTechnologies &&
                  v.transformingTechnologies.map((ele) => {
                    checkNodes2.push(ele.techId);
                    count2 = {};
                    for (const element of checkNodes2) {
                      if (count2[element]) {
                        count2[element] += 1;
                      } else {
                        count2[element] = 1;
                      }
                    }
                  });
              });
            let checked = Object.entries(count);
            let commonIds = [];
            checked.map((d) => {
              if (selectedCompanies.length == d[1]) {
                commonIds.push(d[0]);
              }
            });

            let transformingtechnology = count2 && Object.entries(count2);
            let transformingtechnologyCommanId = [];
            transformingtechnology &&
              transformingtechnology.map((d) => {
                if (selectedCompanies.length == d[1]) {
                  transformingtechnologyCommanId.push(d[0]);
                }
              });

            setBulkCheck([
              ...companyTaxonomy.valueChainCheckedNodes,
              ...commonIds,
              ...companyTaxonomy.transformingCheckedNodes,
              ...transformingtechnologyCommanId,
            ]);
            dispatch(setValueChainCheckedNodes([...companyTaxonomy.valueChainCheckedNodes, ...commonIds]));
            setTaxonomyState([...commonIds]);
            setTaxonomyTransformationState([...transformingtechnologyCommanId]);
            dispatch(setTransformingCheckedNodes([...companyTaxonomy.transformingCheckedNodes, ...transformingtechnologyCommanId]));
          });
        })
        .catch((err) => {
          console.log("err fetchProductTaxonomy :: ", err);
        });
    }
  };

  const addValueChainCheckedNodes = (id) => {
    let taxonomyCheck = companyTaxonomy.valueChainCheckedNodes;
    let taxonomyUnCheck = companyTaxonomy.valueChainUnCheckedNodes;

    // for initial nodes
    let initialCheckedNodes = companyTaxonomy.initialValueChainCheckedNodes;
    if (initialCheckedNodes.includes(id)) {
      initialCheckedNodes.splice(initialCheckedNodes.indexOf(id), 1);
      dispatch(setInitialCheckedValueChainNodes(initialCheckedNodes));
    }
    dispatch(setInitialCheckedValueChainNodes([...companyTaxonomy.initialValueChainCheckedNodes, id]));

    if (taxonomyCheck.includes(id)) {
      taxonomyCheck.splice(taxonomyCheck.indexOf(id), 1);
      dispatch(setValueChainCheckedNodes(taxonomyCheck));
      if (bulkCheck.includes(id)) {
        dispatch(setValueChainUnCheckedNodes([...companyTaxonomy.valueChainUnCheckedNodes, id]));
      }
      return;
    }
    if (taxonomyUnCheck.includes(id)) {
      taxonomyUnCheck.splice(taxonomyUnCheck.indexOf(id), 1);
      dispatch(setValueChainUnCheckedNodes(taxonomyUnCheck));
    }

    dispatch(setValueChainCheckedNodes([...companyTaxonomy.valueChainCheckedNodes, id]));
  };

  const addValueChainTransformingCheckedNodes = (id) => {
    let taxonomyCheck = companyTaxonomy.transformingCheckedNodes;
    let taxonomyUnCheck = companyTaxonomy.transformingUnCheckedNodes;
    if (taxonomyCheck.includes(id)) {
      taxonomyCheck.splice(taxonomyCheck.indexOf(id), 1);
      dispatch(setTransformingCheckedNodes(taxonomyCheck));
      if (bulkCheck.includes(id)) {
        dispatch(setTransformingUnCheckedNodes([...companyTaxonomy.transformingUnCheckedNodes, id]));
      }
      return;
    }
    if (taxonomyUnCheck.includes(id)) {
      taxonomyUnCheck.splice(taxonomyUnCheck.indexOf(id), 1);
      dispatch(setTransformingUnCheckedNodes(taxonomyUnCheck));
    }
    dispatch(setTransformingCheckedNodes([...companyTaxonomy.transformingCheckedNodes, id]));
  };

  /* 
        @Description : Bulk tagging trend sgf - Insider
    */
  const handleSubmitTrend = async (fetchGridData) => {
    let payload = {
      companyIds: initalState,
      taxonomyTagging: companyTaxonomy.trendCheckedNodes,
      taxonomyUnTagging: companyTaxonomy.trendUnCheckedNodes,
      sgfIds: companyTaxonomy.initialSgfCheckedNodes,
    };

    if (companyTaxonomy?.initialTrendCheckedNodes.length == 0) {
      try {
        dispatch(
          showAlertBox({
            okCallback: async () => {
              setTimeout(() => {
                dispatch(hideLoader());
              }, 0);
            },
            cancelCallback: async () => {
              setShowTaxonomyBulk(false);
            },

            okText: "Ok",
            cancelText: "Cancel",
            content: "There is no change observed in taxonomy tagging, press Ok to continue or press Cancel to close.",
            title: "dialogAlertCssWarning",
          })
        );
      } catch (error) {
        dispatch(actionError("Something Went Wrong...!"));
      }
    } else {
      try {
        dispatch(
          showAlertBox({
            okCallback: async () => {
              setTimeout(() => {
                dispatch(showLoader());
                saveCompanyTrendTags(payload)
                  .then((result) => {
                    dispatch(actionSuccess("Taxonomy updated successfully"));
                    dispatch(setResetRedux());
                    setShowTaxonomyBulk(false);
                    dispatch(hideLoader());
                    fetchGridData();
                  })
                  .catch((err) => {
                    dispatch(hideLoader());
                    dispatch(
                      showAlertBox({
                        okCallback: async () => {
                          setTimeout(() => {
                            dispatch(hideLoader());
                            setShowTaxonomyBulk(false);
                            dispatch(setResetRedux());
                          }, 0);
                        },
                        okText: "Ok",
                        hideCancelButton: true,
                        content:
                          "Some of the selected companies has products, taxonomy can not be untagged from such companies. You need to untagged taxonomy from their products.",
                        title: "dialogAlertCssWarning",
                      })
                    );
                  });
              }, 0);
            },
            okText: "Submit",
            cancelText: "Cancel",
            content: "Are you sure you want to update the tagging?",
            title: "dialogAlertCssWarning",
          })
        );
      } catch (error) {
        dispatch(actionError("Something went wrong...!"));
      }
    }
  };

  const fetchTrendTagging = () => {
    setCssClass("treeClass trend");
    setListClass("select-lists bulktagging");
    const selectedCompanies = localStorage.getItem("selectedCompanies") ? JSON.parse(localStorage.getItem("selectedCompanies")) : [];
    if (selectedCompanies.length) {
      setBulkCheck([]);
      const params = { limit: gridState.recordsPerPage };
      params.filters = [
        ["productVariant.productName", "eq", productVariant],
        ["_id", "in", selectedCompanies],
      ];
      params.fields = ["companyIndustryTrendsTags"];
      const contentTaxonomy = fetchCompany(params);
      contentTaxonomy
        .then((res) => {
          let checkNodes = [];
          let count;
          res.data.data.map((value) => {
            value.companyIndustryTrendsTags.length > 0 &&
              value.companyIndustryTrendsTags.map((v) => {
                checkNodes.push(v.trendId);
                count = {};
                for (const element of checkNodes) {
                  if (count[element]) {
                    count[element] += 1;
                  } else {
                    count[element] = 1;
                  }
                }
              });
            let checked = Object.entries(count);
            let commonIds = [];
            checked.map((d) => {
              if (selectedCompanies.length == d[1]) {
                commonIds.push(d[0]);
              }
            });
            setBulkCheck([...companyTaxonomy.trendCheckedNodes, ...commonIds]);
            setTaxonomyState([...commonIds]);
            dispatch(setTrendCheckedNodes([...companyTaxonomy.trendCheckedNodes, ...commonIds]));
          });
        })
        .catch((err) => {
          console.log("err fetchProductTaxonomy :: ", err);
        });
    }
  };

  const trendBulkTagging = async () => {
    dispatch(visitRecord());
    const selectedCompanies = localStorage.getItem("selectedCompanies") ? JSON.parse(localStorage.getItem("selectedCompanies")) : [];
    if (selectedCompanies.length) {
      const params = {};
      params.filters = [
        ["productVariant.productName", "eq", productVariant],
        ["_id", "in", selectedCompanies],
      ];
      const companyIsLive = await fetchCompany(params);
      dispatch(hideLoader());
      if (companyIsLive) {
        dispatch(showLoader());
        setInitialState(selectedCompanies);
        setShowTaxonomyBulk(true);
      }
    } else {
      dispatch(actionError("Please select company."));
    }
  };

  const addTrendCheckedNodes = (taxonomyId) => {
    let id = taxonomyId.split("-")[1];
    let sgfId = taxonomyId.split("-")[0];
    let taxonomyCheck = companyTaxonomy.trendCheckedNodes;
    let taxonomyUnCheck = companyTaxonomy.trendUnCheckedNodes;

    // for initial nodes
    let initialCheckedNodes = companyTaxonomy.initialTrendCheckedNodes;
    if (initialCheckedNodes.includes(id)) {
      initialCheckedNodes.splice(initialCheckedNodes.indexOf(id), 1);
      dispatch(setInitialCheckedTrendNodes(initialCheckedNodes));
    }
    dispatch(setInitialCheckedTrendNodes([...companyTaxonomy.initialTrendCheckedNodes, id]));

    // set checked sgf node
    dispatch(setInitialCheckedSgfNodes([...companyTaxonomy.initialSgfCheckedNodes, sgfId]));

    if (taxonomyCheck.includes(id)) {
      taxonomyCheck.splice(taxonomyCheck.indexOf(id), 1);
      dispatch(setTrendCheckedNodes(taxonomyCheck));
      if (bulkCheck.includes(id)) {
        dispatch(setTrendUnCheckedNodes([...companyTaxonomy.trendUnCheckedNodes, id]));
      }
      return;
    }
    if (taxonomyUnCheck.includes(id)) {
      taxonomyUnCheck.splice(taxonomyUnCheck.indexOf(id), 1);
      dispatch(setTrendUnCheckedNodes(taxonomyUnCheck));
    }
    dispatch(setTrendCheckedNodes([...companyTaxonomy.trendCheckedNodes, id]));
  };

  /* 
    @Description : Bulk tagging for business objective
    */
  const handleSubmitObjective = async (fetchGridData) => {
    let taxonomy = {
      companyIds: initalState,
      companyObjectiveTaxonomy: companyTaxonomy.objectiveTaxonomyCheckedNodes,
      companyUnCheckObjectiveTaxonomy: companyTaxonomy.objectiveTaxonomyUnCheckedNodes,
    };

    if (companyTaxonomy?.initialObjectiveCheckedNodes.length == 0) {
      try {
        dispatch(
          showAlertBox({
            okCallback: async () => {
              setTimeout(() => {
                dispatch(hideLoader());
              }, 0);
            },
            cancelCallback: async () => {
              setShowTaxonomyBulk(false);
            },

            okText: "Ok",
            cancelText: "Cancel",
            content: "There is no change observed in taxonomy tagging, press Ok to continue or press Cancel to close.",
            title: "dialogAlertCssWarning",
          })
        );
      } catch (error) {
        dispatch(actionError("Something Went Wrong...!"));
      }
    } else {
      try {
        dispatch(
          showAlertBox({
            okCallback: () => {
              setTimeout(() => {
                dispatch(showLoader());
                saveCompanyBusinessObjectiveTaxonomy(taxonomy)
                  .then((result) => {
                    dispatch(actionSuccess("Taxonomy updated successfully"));
                    dispatch(setResetRedux());
                    setShowTaxonomyBulk(false);
                    dispatch(hideLoader());
                    fetchGridData();
                  })
                  .catch((err) => {
                    dispatch(hideLoader());
                    dispatch(setResetRedux());
                    setShowTaxonomyBulk(true);
                    dispatch(actionError("Please select company"));
                  });
              }, 0);
            },
            okText: "Submit",
            cancelText: "Cancel",
            content: "Are you sure you want to update the tagging?",
            title: "dialogAlertCssWarning",
          })
        );
      } catch (error) {
        dispatch(actionError("Something went wrong...!"));
      }
    }
  };

  const fetchObjectiveTagging = () => {
    setListClass("select-lists bulktagging");
    setCssClass("treeClass");
    const selectedCompanies = localStorage.getItem("selectedCompanies") ? JSON.parse(localStorage.getItem("selectedCompanies")) : [];
    if (selectedCompanies?.length) {
      setBulkCheck([]);
      const params = { limit: gridState.recordsPerPage };
      params.filters = [
        ["productVariant.productName", "eq", productVariant],
        ["_id", "in", selectedCompanies],
      ];
      params.fields = ["companyObjectiveTaxonomy"];
      const contentTaxonomy = fetchCompany(params);
      contentTaxonomy
        .then((res) => {
          let checkNodes = [];
          let count;
          res.data.data.map((value) => {
            value.companyObjectiveTaxonomy.length > 0 &&
              value.companyObjectiveTaxonomy.map((v) => {
                checkNodes.push(v.taxonomyId);
                count = {};
                for (const element of checkNodes) {
                  if (count[element]) {
                    count[element] += 1;
                  } else {
                    count[element] = 1;
                  }
                }
              });

            let checked = Object.entries(count);
            let commonIds = [];
            checked.map((d) => {
              if (selectedCompanies.length == d[1]) {
                commonIds.push(d[0]);
              }
            });

            setBulkCheck([...companyTaxonomy.objectiveTaxonomyCheckedNodes, ...commonIds]);
            setTaxonomyState([...commonIds]);
            dispatch(setObjectiveTaxonomyCheckedNodes([...companyTaxonomy.objectiveTaxonomyCheckedNodes, ...commonIds]));
          });
        })
        .catch((err) => {
          console.log("err fetchProductTaxonomy :: ", err);
        });
    }
  };

  const addObjectiveCheckedNodes = (id) => {
    let taxonomyCheck = companyTaxonomy.objectiveTaxonomyCheckedNodes;
    let taxonomyUnCheck = companyTaxonomy.objectiveTaxonomyUnCheckedNodes;

    // for initial nodes
    let initialCheckedNodes = companyTaxonomy.initialObjectiveCheckedNodes;
    if (initialCheckedNodes.includes(id)) {
      initialCheckedNodes.splice(initialCheckedNodes.indexOf(id), 1);
      dispatch(setInitialCheckedObjectiveNodes(initialCheckedNodes));
    }
    dispatch(setInitialCheckedObjectiveNodes([...companyTaxonomy.initialObjectiveCheckedNodes, id]));

    if (taxonomyCheck.includes(id)) {
      taxonomyCheck.splice(taxonomyCheck.indexOf(id), 1);
      dispatch(setObjectiveTaxonomyCheckedNodes(taxonomyCheck));
      if (bulkCheck.includes(id)) {
        dispatch(setObjectiveTaxonomyUnCheckedNodes([...companyTaxonomy.objectiveTaxonomyUnCheckedNodes, id]));
      }
      return;
    }
    if (taxonomyUnCheck.includes(id)) {
      taxonomyUnCheck.splice(taxonomyUnCheck.indexOf(id), 1);
      dispatch(setObjectiveTaxonomyUnCheckedNodes(taxonomyUnCheck));
    }
    dispatch(setObjectiveTaxonomyCheckedNodes([...companyTaxonomy.objectiveTaxonomyCheckedNodes, id]));
  };
  // Custom tab name for filter by taxonomy and taxonomy bluk tagging
  let tabNames;
  if (productVariant == PRODUCT_VARIANT.INSIDER) {
    tabNames = taxonomyTaggingTabName ? ["Tag Industry"] : ["By Industry"];
  } else {
    tabNames = taxonomyTaggingTabName
      ? ["Tag Technology", "Tag Key Impact Area", "Tag Value Chain", "Tag Business Objective"]
      : ["By Technology", "By Key Impact Area", "By Value Chain", "By Business Objective"];
  }

  // handle save for taxonomy bulk tagging
  const handleSave = (selectedIndex, fetchGridData) => {
    if (productVariant === PRODUCT_VARIANT.WHATNEXT || productVariant === PRODUCT_VARIANT.COGNITION) {
      if (selectedIndex == 0) {
        handleSubmitTechnology(fetchGridData);
      }
      if (selectedIndex == 1) {
        handleSubmitKia(fetchGridData);
      }
      if (selectedIndex == 2) {
        handleSubmitValueChain(fetchGridData);
      }
      if (selectedIndex == 3) {
        handleSubmitObjective(fetchGridData);
      }
    }
    if (productVariant === PRODUCT_VARIANT.INSIDER) {
      if (selectedIndex == 0) {
        handleSubmitTrend(fetchGridData);
      }
      if (selectedIndex == 1) {
        handleSubmitObjective(fetchGridData);
      }
    }
  };

  const handleTaxonomyTagging = (args, selectedIndex) => {
    let valueId = args?.data[0].id;
    if (productVariant === PRODUCT_VARIANT.WHATNEXT || productVariant === PRODUCT_VARIANT.COGNITION) {
      if (selectedIndex == 0) {
        addTaxonomyCheckedNodes(valueId);
      }
      if (selectedIndex == 1) {
        addKiaCheckedNodes(valueId);
      }
      if (selectedIndex == 2) {
        handleValueChainValidation(args);
      }
      if (selectedIndex == 3) {
        addObjectiveCheckedNodes(valueId);
      }
    }
    if (productVariant === PRODUCT_VARIANT.INSIDER) {
      if (selectedIndex == 0) {
        addTrendCheckedNodes(valueId);
      }
      if (selectedIndex == 1) {
        addObjectiveCheckedNodes(valueId);
      }
    }
  };

  const handleValueChainValidation = (args) => {
    if (args?.data[0]?.parentID == null) {
      addValueChainCheckedNodes(args?.data[0]?.id);
    }
    if (args?.data[0]?.parentID !== null) {
      addValueChainTransformingCheckedNodes(args?.data[0]?.id);
    }
  };

  // fetch TaxonomyTagging data for taxonomy bluk tagging
  const fetchTaxonomyTagging = (selectedIndex) => {
    if (productVariant === PRODUCT_VARIANT.WHATNEXT || productVariant === PRODUCT_VARIANT.COGNITION) {
      if (selectedIndex == 0) {
        fetchTechnologyTagging();
      }
      if (selectedIndex == 1) {
        fetchKiaTagging();
      }
      if (selectedIndex == 2) {
        fetchValueChainTagging();
      }
      if (selectedIndex == 3) {
        fetchObjectiveTagging();
      }
    }
    if (productVariant === PRODUCT_VARIANT.INSIDER) {
      if (selectedIndex == 0) {
        fetchTrendTagging();
      }
      if (selectedIndex == 1) {
        fetchObjectiveTagging();
      }
    }
  };

  const setBulkCheckedNode = (selectedIndex) => {
    if (productVariant === PRODUCT_VARIANT.WHATNEXT || productVariant === PRODUCT_VARIANT.COGNITION) {
      selectedIndex == 0 && setBulkCheck([...companyTaxonomy.taxonomyCheckedNodes]);
      selectedIndex == 1 && setBulkCheck([...companyTaxonomy.kiaCheckedNodes]);
      selectedIndex == 2 && setBulkCheck([...companyTaxonomy.valueChainCheckedNodes, ...companyTaxonomy.transformingCheckedNodes]);
      selectedIndex == 3 && setBulkCheck([...companyTaxonomy.objectiveTaxonomyCheckedNodes]);
    }
    if (productVariant === PRODUCT_VARIANT.INSIDER) {
      selectedIndex == 0 && setBulkCheck([...companyTaxonomy.trendCheckedNodes]);
      selectedIndex == 1 && setBulkCheck([...companyTaxonomy.objectiveTaxonomyCheckedNodes]);
    }
  };

  const resetAllTaxonnomy = (selectedIndex) => {
    if (productVariant === PRODUCT_VARIANT.WHATNEXT || productVariant === PRODUCT_VARIANT.COGNITION) {
      if (selectedIndex == 0) {
        dispatch(setTaxonomyCheckedNodes([...taxonomyState]));
      }
      if (selectedIndex == 1) {
        dispatch(setKiaCheckedNodes([...taxonomyState]));
      }
      if (selectedIndex == 2) {
        dispatch(setValueChainCheckedNodes([...taxonomyState]));
        dispatch(setTransformingCheckedNodes([...taxonomyTransformationState]));
      }
      if (selectedIndex == 3) {
        dispatch(setObjectiveTaxonomyCheckedNodes([...taxonomyState]));
      }
    }
    if (productVariant === PRODUCT_VARIANT.INSIDER) {
      if (selectedIndex == 0) {
        dispatch(setTrendCheckedNodes([...taxonomyState]));
      }
      if (selectedIndex == 1) {
        dispatch(setObjectiveTaxonomyCheckedNodes([...taxonomyState]));
      }
    }
    setBulkCheck([...taxonomyState]);
  };

  // reset redux
  useEffect(() => {
    if (window.location.href === `${window.location.origin}/company`) {
      dispatch(resetCompany());
    }
  }, [props]);

  return (
    <div className="gennx-content-wrapper content-wrapper px-3">
      <div className="gennx-grid-container">
        {techTrendData && (
          <CommonDataGrid
            setFilterData={setFilterData}
            filterData={filterData}
            isChildGrid={false}
            fetch={fetchCompanyWithProductVariant}
            showLoader={showLoaderGrid}
            hideLoader={hideLoaderGrid}
            columnNames={columnNames}
            columnFields={columnFields}
            formatRowData={formatRowData}
            deleteId={deleteId}
            uploadRight={accessActionImport}
            exportRight={accessActionExport}
            isExport={exportData}
            getMultiSelectedRows={(data) => {
              localStorage.setItem("selectedCompanies", JSON.stringify([...data]));
            }}
            getMultiDeSelectedRows={(data) => {
              localStorage.setItem("selectedCompanies", JSON.stringify([...data]));
            }}
            deleteMany={accessActionDelete ? deleteManyFunc : null}
            objectiveTaxonomyRight={accessActionTaxonomyBulkTagging}
            objectiveTaxonomyTag={objectiveTaxonomyTag}
            addNewRight={accessActionListingAddNew}
            addNew={() => history.push(`/company/add#basic`)}
            showFilterByTaxonomy={productVariant !== PRODUCT_VARIANT.CHEERSIN && accessActionFilterByTaxonomy}
            moduleName={moduleName}
            boTagging={true}
            //bulk tagging
            checked={bulkCheck}
            setBulkCheck={setBulkCheck}
            showTaxonomyBulk={showTaxonomyBulk}
            setShowTaxonomyBulk={setShowTaxonomyBulk}
            taxonomyBulkTagging={taxonomyBulkTagging}
            trendBulkTagging={trendBulkTagging}
            cssClass={cssClass}
            setCssClass={setCssClass}
            listClass={listClass}
            setListClass={setListClass}
            tabName={tabNames}
            handleTaxonomyTabName={setTaxonomyTaggingTabName}
            customDialogBoxClass="dialoagBoxClass"
            customArrowClass="arrow-top"
            bulkTaggingArrowTop="bulkTaggingArrowTop"
            handleSubmit={handleSave}
            handleTaxonomyTagging={handleTaxonomyTagging}
            fetchTaxonomyTagging={fetchTaxonomyTagging}
            setBulkCheckedNode={setBulkCheckedNode}
            resetAllTaxonnomy={resetAllTaxonnomy}
            valueChainData={valueChainData}
            accessPermissionBulk={accessActionTaxonomyBulkTagging}
          />
        )}
      </div>
    </div>
  );
};
CompanyDashBoard.propTypes = {
  accessRights: PropTypes.arrayOf(PropTypes.string),
  formik: PropTypes.object,
  computedMatch: PropTypes.string,
  interfaceActionAccess: PropTypes.array,
};
export default CompanyDashBoard;
